import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { TopHeaderForAgent } from 'ui/components/panes/Header/TopHeaderForAgent';
import { TopFooter } from 'ui/components/panes/Footer/TopFooter';
import { KvPictures } from 'ui/components/public/patterns/TopForAgent/KvPictures';
import { KvMainText } from 'ui/components/public/patterns/TopForAgent/KvMainText';
import { Issue } from 'ui/components/public/patterns/TopForAgent/Issue';
import { Lead } from 'ui/components/public/patterns/TopForAgent/Lead';
import { Announcements } from 'ui/components/public/patterns/TopForAgent/Announcements';
import { AboutService } from 'ui/components/public/patterns/TopForAgent/AboutService';
import { Function } from 'ui/components/public/patterns/TopForAgent/Function';
import { Plan } from 'ui/components/public/patterns/TopForAgent/Plan';
import { QA } from 'ui/components/public/patterns/TopForAgent/QA';
import { Offer } from 'ui/components/public/patterns/TopForAgent/Offer';
import KvPcImg from 'ui/images/top_for_agent/kv_bg_pc.svg';
import KvSpImg from 'ui/images/top_for_agent/kv_bg_sp.svg';
import { mqMin } from 'utils/responsive';
import { useLocation } from 'react-router-dom';

const Wrapper = styled.div`
    font-weight: 700;
`;

const MainSection = styled.section`
    position: relative;
    width: 100%;
    max-height: 605px;
    aspect-ratio: 86 / 121;
    background-image: url(${KvSpImg});
    background-repeat: no-repeat;
    background-size: 1190px;
    background-position: top center;
    overflow: hidden;

    ${mqMin('SP')} {
        background-size: 1700px;
    }

    ${mqMin('NARROW')} {
        background-image: url(${KvPcImg});
        width: 100%;
        height: 700px;
        max-height: initial;
        aspect-ratio: initial;
        background-size: 2640px auto;
        background-position: bottom center;
        padding-top: 0;
        padding-bottom: 127px;
        overflow: hidden;
    }

    img {
        display: block;
        max-width: 100%;
    }

    @keyframes kvFadeinZoom {
        0% {
            opacity: 0;
            scale: 0.9;
        }
        100% {
            opacity: 1;
            scale: 1;
        }
    }
`;

const KvLinkSp = styled.div`
    background-color: var(--color-navy);
    padding: 38px 0 34px 0;

    p {
        text-align: center;
        color: #fff;
        font-size: 1.25rem; /*20px*/
        line-height: 1.7em;
        letter-spacing: 0.1em;
        margin: 0 auto 16px auto;

        strong {
            position: relative;
            font-size: 1.5rem;

            &::after {
                position: absolute;
                top: -20px;
                left: 50%;
                content: '・';
                transform: translateX(-50%);
                font-size: 20px;
            }
        }
    }

    a {
        display: block;
        background-color: var(--color-orange);
        border-radius: 8px;
        border: solid 2px #fff;
        color: #fff;
        text-align: center;
        text-decoration: none;
        padding: 16px 20px;
        margin: 0 auto 0 auto;
        transition: all 0.3s;

        &:hover {
            background-color: #fff;
            border: solid 2px var(--color-orange);
            color: var(--color-orange);
        }

        width: 287px;
        position: relative;
        overflow: hidden;
        margin: 0 auto 0 auto;

        &::before {
            position: absolute;
            content: '';
            display: inline-block;
            top: -180px;
            left: 0;
            width: 30px;
            height: 100%;
            background-color: rgba(255, 236, 227, 0.8);
            transition: 300ms;
            animation: kvBtn 1s ease-in-out 1.6s;
        }
    }

    ${mqMin('NARROW')} {
        display: none;
    }
`;

export const TopForAgent: React.FC = () => {
    const [ticking, setTicking] = useState(false);
    const fadeItems = useRef<NodeListOf<Element> | null>(null);
    const fadeItemsLi = useRef<NodeListOf<Element> | null>(null);
    const location = useLocation();

    useEffect(() => {
        // topページにのみ適用するcssを追加
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/top_agent.css';
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, []);

    useEffect(() => {
        // フェードイン処理
        fadeItems.current = document.querySelectorAll('.fadein');
        fadeItemsLi.current = document.querySelectorAll('.fadeinLi');

        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const fadePoint = 350;

            fadeItems.current?.forEach((e) => {
                const itemTop = e.getBoundingClientRect().top + scrollTop;
                if (
                    scrollTop + windowHeight - fadePoint > itemTop ||
                    scrollTop + windowHeight >= documentHeight - 100
                ) {
                    e.classList.add('scroll_on');
                }
            });

            fadeItemsLi.current?.forEach((e) => {
                const itemTop = e.getBoundingClientRect().top + scrollTop;
                const childfadeItems = Array.from(e.children);
                if (scrollTop + fadePoint > itemTop) {
                    childfadeItems.forEach((elm, i) => {
                        setTimeout(() => {
                            elm.classList.add('scroll_on');
                        }, 300 * i);
                    });
                }
            });
        };

        function onScroll() {
            if (!ticking) {
                requestAnimationFrame(handleScroll);
                setTicking(true);
            }
        }

        window.addEventListener('scroll', onScroll);
        handleScroll();

        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    useEffect(() => {
        if (location.hash) {
            const targetId = location.hash.replace('#', '');
            const el = document.getElementById(targetId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [location.hash]);

    const navigate = useNavigate();
    const goToRegister = () => {
        navigate('/send-registration-mail/');
    };

    return (
        <Wrapper>
            <TopHeaderForAgent />
            <MainSection>
                <>
                    <KvPictures />
                    <KvMainText />
                </>
            </MainSection>
            <KvLinkSp>
                <p>
                    マッチング成立までは<strong>基</strong>
                    <strong>本</strong>
                    <strong>無</strong>
                    <strong>料</strong>
                </p>
                <a href="#" onClick={() => goToRegister()}>
                    無料登録して企業を探す
                </a>
            </KvLinkSp>
            <main id="home">
                <Issue />
                <Lead />
                <Announcements />
                <AboutService />
                <Function />
                <Plan />
                <QA />
                <Offer />
            </main>
            <TopFooter />
        </Wrapper>
    );
};
